import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { getPaceDisplay } from "../../../../constants/pace";
import {
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import * as Yup from "yup";
import { Classroom, Pace } from "../../../../types/ClassroomModel";
import { EapProgram } from "../../../../types/School";
import dayjs, { Dayjs } from "dayjs"; // Import Dayjs and types
import {
  calculateEndDate,
  formatDateForDisplay,
  getTodayStart,
  toLocalDate,
} from "../../../../utils/dateUtils";

interface ClassroomFormProps {
  selectedRow?: Classroom;
  onSubmit: (values: Classroom) => Promise<void>;
}

const ClassroomForm: React.FC<ClassroomFormProps> = ({
  selectedRow,
  onSubmit,
}) => {
  const programLengths: Record<EapProgram, number> = {
    [EapProgram.EAP_YELLOW]: 20,
    [EapProgram.EAP_GREEN]: 20,
    [EapProgram.EAP_ACCESS]: 12,
  };

  const initialValues = React.useMemo(() => {
    const program = selectedRow?.program || EapProgram.EAP_YELLOW;
    const pace = selectedRow?.pace || 7;
    const startDate = selectedRow
      ? toLocalDate(selectedRow.startDate) || new Date()
      : new Date();
    // For new classrooms, derive endDate from startDate/pace/program length
    // rather than defaulting to today (which makes start == end).
    const initialEndDate = selectedRow
      ? toLocalDate(selectedRow.endDate) ||
        calculateEndDate(startDate, pace, programLengths[program]) ||
        new Date()
      : calculateEndDate(startDate, pace, programLengths[program]) ||
        new Date();
    return {
      ...(selectedRow?._id ? { _id: selectedRow._id } : {}),
      program,
      classroomName: selectedRow?.classroomName || "",
      startDate,
      endDate: initialEndDate,
      pace,
      students: selectedRow?.students || [],
      isActive: selectedRow?.isActive ?? true,
      ...(selectedRow?.teacher ? { teacher: selectedRow.teacher } : null),
    };
  }, [selectedRow]); // Only recalculate when `selectedRow` changes

  const formik = useFormik({
    initialValues,
    enableReinitialize: true, // If you still want to reinitialize on `selectedRow` change
    validationSchema: Yup.object({
      program: Yup.string().oneOf(Object.values(EapProgram)),
      classroomName: Yup.string().required("Classroom name is required"),
      startDate: Yup.date()
        .min(getTodayStart(), "Start date cannot be in the past")
        .required("Required"),

      pace: Yup.number().oneOf([0, 3, 7, 14]).required("Required"),
      students: Yup.array(),
    }),
    onSubmit: async (values: Classroom) => {
      // Recompute endDate from the final form values so the persisted value
      // always matches what the read-only "End Date" field shows.
      const resolvedEndDate =
        calculateEndDate(
          values.startDate,
          values.pace,
          programLengths[values.program as EapProgram]
        ) || values.endDate;
      const finalValues = { ...values, endDate: resolvedEndDate };
      await onSubmit(finalValues);
      setTimeout(() => {
        formik.resetForm({ values: finalValues });
      }, 0);
    },
  });

  // Auto-fill classroom name when program changes and classroom name is empty
  useEffect(() => {
    if (formik.values.program && !formik.values.classroomName) {
      // Format the program name to be more readable (remove EAP_ prefix and replace _ with space)
      const formattedProgramName = formik.values.program
        .replace("EAP_", "")
        .replace("_", " ");
      formik.setFieldValue("classroomName", formattedProgramName);
    }
  }, [formik.values.program]);

  // 0 = no progression lock: all weeks open from day one (self-paced).
  // Backend treats pace === 0 as testing mode in updateWeeklyContentStatus.
  const paceOptions: Pace[] = [0, 3, 7, 14];
  const programOptions: EapProgram[] = Object.values(EapProgram);

  const [loading, setLoading] = useState(false);

  const handleDateChange = (field: string, value: Dayjs | null) => {
    const date = value ? value.toDate() : null;
    formik.setFieldValue(field, date);
    // Keep endDate in sync when startDate changes — without this the form
    // submits whatever endDate was at init (today for new classrooms),
    // resulting in startDate === endDate.
    if (field === "startDate" && date) {
      const endDate = calculateEndDateFromValues(date);
      formik.setFieldValue("endDate", endDate);
    }
  };

  // Helper function to calculate end date from current form values
  const calculateEndDateFromValues = (startDateOverride?: Date): Date => {
    const { startDate, pace, program } = formik.values;
    const dateToUse = startDateOverride || startDate;
    const programLength = programLengths[program as EapProgram];

    // Pace 0 (no lock) has no cadence — use the standard weekly pace for a
    // sensible course window (endDate is informational; nothing locks).
    const effectivePace = pace === 0 ? 7 : pace;

    const endDate = calculateEndDate(dateToUse, effectivePace, programLength);
    return endDate || new Date();
  };

  // For display in the read-only field
  const getFormattedEndDate = (): string => {
    const endDate = calculateEndDateFromValues();
    return formatDateForDisplay(endDate, "dd-MM-yyyy");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Program Field */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel id="program-label" shrink>
                Program
              </InputLabel>
              <Select
                labelId="program-label"
                id="program"
                name="program"
                value={formik.values.program}
                label="Program"
                onChange={(event) => {
                  formik.setFieldValue("program", event.target.value);
                  // Recalculate end date when program changes
                  const endDate = calculateEndDateFromValues();
                  formik.setFieldValue("endDate", endDate);
                }}
              >
                {programOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option.replace("EAP_", "").replace("_", " ")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Classroom Name Field */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              id="classroomName"
              name="classroomName"
              label="Classroom Name"
              value={formik.values.classroomName}
              onChange={formik.handleChange}
              error={
                formik.touched.classroomName &&
                Boolean(formik.errors.classroomName)
              }
              helperText={
                formik.touched.classroomName && formik.errors.classroomName
              }
              onBlur={formik.handleBlur}
              autoComplete="off"
            />
          </Grid>

          {/* Start Date Field */}
          <Grid item xs={12}>
            <DatePicker
              label="Start Date"
              value={dayjs(formik.values.startDate)}
              onChange={(value) => handleDateChange("startDate", value)}
              minDate={dayjs(getTodayStart())}
              format="DD-MM-YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  error:
                    formik.touched.startDate &&
                    Boolean(formik.errors.startDate),
                  helperText:
                    formik.touched.startDate &&
                    typeof formik.errors.startDate === "string"
                      ? formik.errors.startDate
                      : undefined,
                },
              }}
            />
          </Grid>

          {/* End Date Field */}
          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              id="endDate"
              name="endDate"
              label="End Date"
              value={getFormattedEndDate()}
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>

          {/* Pace Field */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel id="pace-label">Pace</InputLabel>
              <Select
                labelId="pace-label"
                id="pace"
                name="pace"
                value={formik.values.pace}
                label="Pace"
                onChange={(event) => {
                  formik.setFieldValue("pace", event.target.value);
                  // Recalculate end date when pace changes
                  const endDate = calculateEndDateFromValues();
                  formik.setFieldValue("endDate", endDate);
                }}
              >
                {paceOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {getPaceDisplay(option)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Is Active Field */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  id="isActive"
                  name="isActive"
                  checked={formik.values.isActive}
                  onChange={(event) =>
                    formik.setFieldValue("isActive", event.target.checked)
                  }
                  color="primary"
                />
              }
              label="Is Active"
            />
          </Grid>
        </Grid>

        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={!formik.isValid || formik.isSubmitting || loading}
          style={{ marginTop: "2rem" }}
        >
          {selectedRow ? "Update Classroom" : "Add new Classroom"}
        </Button>
      </form>

      <Dialog
        open={loading}
        aria-labelledby="loading-dialog-title"
        aria-describedby="loading-dialog-description"
      >
        <DialogTitle id="loading-dialog-title">Processing</DialogTitle>
        <DialogContent>
          <DialogContentText id="loading-dialog-description">
            Please wait while we save your data.
          </DialogContentText>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ClassroomForm;
